import React from 'react';
import {Image, ImageAspectRatioPresets, Text, TextButton, TextButtonPriority} from 'wix-ui-tpa/cssVars';
import {useLocaleKeys} from '../../../locale-keys/LocaleKeys';
import {classes} from './StoreNameAndLogo.st.css';
import {CheckoutHeader} from '@wix/wixstores-graphql-schema-node';
import {SPECS} from '../../constants';
import {useExperiments} from '@wix/yoshi-flow-editor';
import classNames from 'classnames';

export interface StoreNameAndLogoProps {
  continueShoppingUrl?: string;
  clickOnContinueShopping?: () => void;
  logoUrl?: string;
  storeName?: string;
  checkoutHeader?: CheckoutHeader;
  isOneColumnView?: boolean;
}

export enum StoreNameAndLogoDataHooks {
  root = 'StoreNameAndLogoDataHooks.root',
  logo = 'StoreNameAndLogoDataHooks.logo',
  storeName = 'StoreNameAndLogoDataHooks.storeName',
  title = 'StoreNameAndLogoDataHooks.title',
}

export enum Alignment {
  SIDE = 'SIDE',
  CENTER = 'CENTER',
}

export enum LogoSize {
  SMALL = 'SMALL',
  MEDIUM = 'MEDIUM',
  LARGE = 'LARGE',
}

export const StoreNameAndLogo = ({
  continueShoppingUrl,
  clickOnContinueShopping,
  logoUrl,
  storeName,
  checkoutHeader,
  isOneColumnView,
}: // eslint-disable-next-line sonarjs/cognitive-complexity
StoreNameAndLogoProps) => {
  const localKeys = useLocaleKeys();
  const {experiments} = useExperiments();

  const shouldUseCheckoutHeaderOnCheckout = !!checkoutHeader;
  const shouldShowLogo = !!checkoutHeader?.logo?.size;
  const shouldShowStoreName = !shouldShowLogo;
  const alignment = checkoutHeader?.alignment;
  const shouldDisplayCheckoutLabel = checkoutHeader?.displayCheckoutLabel;
  const logoSize = checkoutHeader?.logo?.size;
  const checkoutHeaderAndLogoA11yChanges = experiments.enabled(SPECS.CheckoutHeaderAndLogoA11yChanges);

  const logoHeight = () => {
    /* istanbul ignore else */
    /* istanbul ignore next */
    if (logoSize === LogoSize.MEDIUM) {
      return isOneColumnView ? 56 : 80;
    }
    /* istanbul ignore next */
    if (logoSize === LogoSize.LARGE) {
      return isOneColumnView ? 80 : 148;
    }
    /* istanbul ignore next */
    return isOneColumnView ? 26 : 56;
  };

  const checkoutTitle = () => {
    if ((!storeName && shouldShowStoreName) || (!logoUrl && shouldShowLogo)) {
      return localKeys.checkout.header_default_title();
    }
    if (shouldShowLogo && !shouldDisplayCheckoutLabel) {
      return undefined;
    }
    return localKeys.checkout.header.store_title_suffix();
  };

  const storeNameAndLogoTitle =
    storeName || logoUrl ? localKeys.checkout.header.store_title_suffix() : localKeys.checkout.header_default_title();

  const checkoutHeaderAndLogoOld = () => {
    return shouldUseCheckoutHeaderOnCheckout ? (
      <TextButton
        data-hook={StoreNameAndLogoDataHooks.root}
        className={
          alignment === Alignment.CENTER
            ? /* istanbul ignore next */ classes.headerTitleAndLogoCenterOld
            : classes.headerTitleAndLogoSideOld
        }
        contentClassName={classes.headerTitleAndLogoContentOld}
        as={'a'}
        onClick={() => clickOnContinueShopping?.()}
        href={continueShoppingUrl}>
        {shouldShowLogo && (
          <Image
            data-hook={StoreNameAndLogoDataHooks.logo}
            src={logoUrl}
            width={logoHeight()}
            height={logoHeight()}
            alt={localKeys.checkout.page.siteLogo_altTextDefault()}
          />
        )}
        <span>
          {shouldShowStoreName && (
            <Text className={classes.headerStoreNameOld} data-hook={StoreNameAndLogoDataHooks.storeName}>
              {storeName}
            </Text>
          )}
          <Text className={classes.headerTitleOld} data-hook={StoreNameAndLogoDataHooks.title}>
            {checkoutTitle()}
          </Text>
        </span>
      </TextButton>
    ) : (
      <TextButton
        data-hook={StoreNameAndLogoDataHooks.root}
        className={classes.headerTitleAndLogoOld}
        contentClassName={classes.headerTitleAndLogoContentOld}
        as={'a'}
        onClick={() => clickOnContinueShopping?.()}
        href={continueShoppingUrl}>
        {logoUrl && (
          <Image
            data-hook={StoreNameAndLogoDataHooks.logo}
            className={classes.logo}
            src={logoUrl}
            aspectRatio={ImageAspectRatioPresets.square}
            fluid={true}
            alt={localKeys.checkout.page.siteLogo_altTextDefault()}
          />
        )}
        <span>
          {storeName && !logoUrl && (
            <Text className={classes.headerStoreNameOld} data-hook={StoreNameAndLogoDataHooks.storeName}>
              {storeName}
            </Text>
          )}
          <Text className={classes.headerTitleOld} data-hook={StoreNameAndLogoDataHooks.title}>
            {storeNameAndLogoTitle}
          </Text>
        </span>
      </TextButton>
    );
  };

  const checkoutHeaderAndLogo = () => {
    return shouldUseCheckoutHeaderOnCheckout ? (
      <div
        data-hook={StoreNameAndLogoDataHooks.root}
        className={classNames(
          alignment === Alignment.CENTER
            ? /* istanbul ignore next */ classes.headerTitleAndLogoCenter
            : classes.headerTitleAndLogoSide,
          classes.headerTitleAndLogoContent
        )}>
        {shouldShowLogo && (
          <a
            onClick={() => clickOnContinueShopping?.()}
            href={continueShoppingUrl}
            data-hook={StoreNameAndLogoDataHooks.logo}>
            <Image
              src={logoUrl}
              width={logoHeight()}
              height={logoHeight()}
              alt={localKeys.checkout.header.businessLogo.altText({storeName})}
            />
          </a>
        )}
        {shouldShowStoreName && (
          <TextButton
            as={'a'}
            priority={TextButtonPriority.primary}
            onClick={() => clickOnContinueShopping?.()}
            href={continueShoppingUrl}
            data-hook={StoreNameAndLogoDataHooks.storeName}
            className={classes.headerStoreName}>
            {storeName}
          </TextButton>
        )}
        <h1
          className={classes.headerTitle}
          data-hook={StoreNameAndLogoDataHooks.title}
          aria-label={checkoutTitle() || storeNameAndLogoTitle}>
          {checkoutTitle()}
        </h1>
      </div>
    ) : (
      <div
        data-hook={StoreNameAndLogoDataHooks.root}
        className={classNames(classes.headerTitleAndLogo, classes.headerTitleAndLogoContent)}>
        {logoUrl && (
          <a
            onClick={() => clickOnContinueShopping?.()}
            href={continueShoppingUrl}
            data-hook={StoreNameAndLogoDataHooks.logo}
            className={classes.logo}>
            <Image
              src={logoUrl}
              aspectRatio={ImageAspectRatioPresets.square}
              fluid={true}
              alt={localKeys.checkout.header.businessLogo.altText({storeName})}
            />
          </a>
        )}
        {storeName && !logoUrl && (
          <TextButton
            as={'a'}
            priority={TextButtonPriority.primary}
            onClick={() => clickOnContinueShopping?.()}
            href={continueShoppingUrl}
            data-hook={StoreNameAndLogoDataHooks.storeName}
            className={classes.headerStoreName}>
            {storeName}
          </TextButton>
        )}
        <h1
          className={classes.headerTitle}
          data-hook={StoreNameAndLogoDataHooks.title}
          aria-label={storeNameAndLogoTitle}>
          {storeNameAndLogoTitle}
        </h1>
      </div>
    );
  };

  return checkoutHeaderAndLogoA11yChanges ? checkoutHeaderAndLogo() : checkoutHeaderAndLogoOld();
};
